/**
 * GPX Parser Utilities
 * Client-side GPX file validation and parsing
 */

/**
 * Validate GPX file before upload
 * @param {File} file - File object to validate
 * @returns {Object} {valid: boolean, error: string|null}
 */
export function validateGpxFile(file) {
    if (!file) {
        return { valid: false, error: 'No file selected' };
    }

    // Check file extension
    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.gpx')) {
        return { valid: false, error: 'File must have .gpx extension' };
    }

    // Check file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
        return { valid: false, error: 'File size must be less than 10MB' };
    }

    if (file.size === 0) {
        return { valid: false, error: 'File is empty' };
    }

    return { valid: true, error: null };
}

/**
 * Parse GPX file and extract track points
 * @param {string} xmlText - GPX file content as XML string
 * @returns {Promise<Object>} {points: Array, error: string|null}
 */
export async function parseGpxPoints(xmlText) {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        // Check for parsing errors
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
            return { points: [], error: 'Invalid XML format' };
        }

        // Try to find track points (trkpt)
        let trackPoints = xmlDoc.querySelectorAll('trk > trkseg > trkpt');

        // If no track points, try route points (rtept)
        if (trackPoints.length === 0) {
            trackPoints = xmlDoc.querySelectorAll('rte > rtept');
        }

        if (trackPoints.length < 2) {
            return { points: [], error: 'No track points found (need at least 2 points)' };
        }

        const points = [];
        trackPoints.forEach(pt => {
            const lat = parseFloat(pt.getAttribute('lat'));
            const lon = parseFloat(pt.getAttribute('lon'));

            if (isFinite(lat) && isFinite(lon)) {
                points.push({ lat, lon });
            }
        });

        if (points.length < 2) {
            return { points: [], error: 'Not enough valid coordinates found' };
        }

        return { points, error: null };
    } catch (error) {
        return { points: [], error: `Parse error: ${error.message}` };
    }
}

/**
 * Read file content as text
 * @param {File} file - File to read
 * @returns {Promise<string>} File content
 */
export function readFileAsText(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error('Failed to read file'));
        reader.readAsText(file);
    });
}

/**
 * Get preview information from GPX file
 * @param {File} file - GPX file
 * @returns {Promise<Object>} Preview info {pointCount, error}
 */
export async function getGpxPreview(file) {
    try {
        const validation = validateGpxFile(file);
        if (!validation.valid) {
            return { pointCount: 0, error: validation.error };
        }

        const content = await readFileAsText(file);
        const { points, error } = await parseGpxPoints(content);

        if (error) {
            return { pointCount: 0, error };
        }

        return { pointCount: points.length, error: null };
    } catch (error) {
        return { pointCount: 0, error: error.message };
    }
}
